// The full filters panel for the "published versions" search modal.
//
// This is NOT a third filter panel — it is the listings page's own
// listingsMatch/ListingsFiltersDrawer, driven through its `contextOverride` prop so it
// runs without the ListingsMatchContext god-context (the same escape hatch listingsBoard,
// rentalToSale and the CRM matches tab already use). This file supplies only the three
// things that override needs: a draft SearchState, a setter, and a commit. All shape
// translation lives in sourceCandidateFilters.ts / the matches tab's matchFiltersAdapter.
//
// ⚠ ListingsFiltersDrawer THROWS when given neither `contextOverride` nor the provider,
// so the override is mandatory here, not an optimisation.
//
// Draft-vs-live semantics, deliberately different from the chip row:
// - The chips apply instantly (one control, one change, one search).
// - The drawer is ~15 controls, so it keeps the listings page's batch model: edits go to
//   a local draft and only the drawer's own "חיפוש" button commits them upward, which
//   fires exactly one search instead of one per keystroke.
// The draft is re-seeded from the live filters on every OPEN, so edits made in the chip
// row while the drawer was closed are always reflected — the two surfaces cannot drift.
//
// Nesting: this Drawer is a MUI Modal opened from a Dialog (the search modal) which is
// itself opened from a Dialog (the listing form). MUI's ModalManager stacks them and only
// the topmost enforces focus, which is the same three-deep arrangement the listings page
// already relies on.
import { useEffect, useState } from "react";
import { propertyTypes as allPropertyTypes } from "../../../../@types/sharedSchema";
import { ListingsFiltersDrawer } from "components/_dashboard/listingsMatch/ListingsFiltersDrawer";
import {
  matchFiltersToSearchState,
  drawerDraftToFiltersPatch
} from "components/_dashboard/contacts/detail/tabs/matches/matchFiltersAdapter";
import type { MatchFilters } from "components/_dashboard/contacts/detail/tabs/matches/contactMatchFilters";
import type { SearchState } from "contexts/ListingsMatchContext";
import { CANDIDATE_FROZEN_FILTERS } from "./sourceCandidateFilters";

// The canonical taxonomy — the same list the chip row's סוג נכס editor offers, and the
// exact domain of ProcessedListingData.propertyType. The listings page instead feeds the
// drawer a facet list fetched per mount; here the static list is both cheaper and safer,
// because a type picked in the chips can never be missing from the drawer's dropdown.
const CANDIDATE_PROPERTY_TYPES: string[] = [...allPropertyTypes];

type SourceCandidatesFiltersDrawerProps = {
  open: boolean;
  /** Live filter state — the draft is re-seeded from this on every open. */
  filters: MatchFilters;
  onClose: () => void;
  /** Commit: fired once, when the realtor presses the drawer's search button. */
  onApply: (patch: Partial<MatchFilters>) => void;
};

export default function SourceCandidatesFiltersDrawer({
  open,
  filters,
  onClose,
  onApply
}: SourceCandidatesFiltersDrawerProps): JSX.Element {
  const [draft, setDraft] = useState<SearchState>(() => matchFiltersToSearchState(filters));

  // Re-seed on open only. Re-seeding while OPEN would throw away edits in progress every
  // time the underlying filters changed, so `filters` is deliberately not a dependency.
  useEffect(() => {
    if (!open) return;
    setDraft(matchFiltersToSearchState(filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <ListingsFiltersDrawer
      isDrawerOpen={open}
      onCloseRequest={onClose}
      frozenFilters={CANDIDATE_FROZEN_FILTERS}
      contextOverride={{
        draftSearchState: draft,
        availablePropertyTypes: CANDIDATE_PROPERTY_TYPES,
        // The drawer calls this with partials (and once with a full spread) — merge both.
        setDraftSearchState: (patch) => setDraft((prev) => ({ ...prev, ...patch })),
        // The drawer closes itself right after this returns.
        //
        // `drawerDraftToFiltersPatch` carries one rule worth knowing about here: flipping
        // מכירה/השכרה without touching the price fields OMITS the price keys from the
        // patch, so the caller may re-seed them for the new transaction type. This surface
        // has no per-type budget to re-seed from, so the previous bounds simply persist —
        // which is right, because they were derived from THIS listing's price.
        applyDraftSearchState: () => onApply(drawerDraftToFiltersPatch(draft, filters))
      }}
    />
  );
}
